import { CoachingFaq } from "../api/coachingFaqApi";
import { UserType } from "../recoil/userAtom";

/**
 * Get the appropriate FAQ content based on user properties
 *
 * This function returns different content for FAQ IDs 30 and 31 based on the user's
 * pricing model and subscription status. For other FAQ IDs, it returns the original content.
 *
 * @param faq The original FAQ item
 * @param user The user object containing pricing model and subscription information
 * @returns The FAQ item with the appropriate content
 */
export const getConditionalFaqContent = (
  faq: CoachingFaq,
  user?: UserType
): CoachingFaq => {
  // If no user or no faq, return the original faq
  if (!user || !faq) {
    return faq;
  }

  // Only modify content for FAQ IDs 30 and 31
  if (faq.faqId !== 12 && faq.faqId !== 101 && faq.faqId !== 102) {
    return faq;
  }

  const pricingModel = user.pricingModel;
  const refundPolicyType = user.refundPolicyType;

  // Create a copy of the FAQ to avoid modifying the original
  const modifiedFaq: CoachingFaq = { ...faq };

  if (faq.faqId === 12) {
    if (pricingModel === "eg") {
      modifiedFaq.answer =
        "코치 선생님은 평일 오전 8시부터 오후 7시 사이에 활동 업데이트를 해드리고 있답니다.\n" +
        "\n" +
        "직접 확인 후 다음 활동을 정성껏 준비하는 작업이라, 정확한 예정 시각을 확정하기 어려운 점 양해 부탁드립니다.\n" +
        "\n" +
        "[활동 업데이트 규정]\n" +
        "\n" +
        "플랜별 최대 업데이트 횟수\n" +
        "- 챌린저 플랜: 일 최대 1회\n" +
        "- 마스터 플랜: 일 최대 2회\n" +
        "\n" +
        "업데이트 제공 기준\n" +
        "- 오후 3시 이전까지 파란색 원으로 표시된 주어진 활동지가 모두 완료되었다면, 당일 업데이트\n" +
        "- 오후 3시 이후에 파란색 원으로 표시된 주어진 활동지가 모두 완료되었다면, 다음 업무일 업데이트\n" +
        "\n" +
        "주말과 공휴일은 코치 선생님의 휴무일로 활동 업데이트가 어렵습니다.";
    }
  }

  // FAQ ID 101: Refund Policy
  if (faq.faqId === 101) {
    if (refundPolicyType === "b") {
      modifiedFaq.answer =
        "아래 환불 규정에 따라 환불이 가능합니다.\n" +
        "\n" +
        "[환불 규정]\n" +
        "\n" +
        "최초 이용\n" +
        "- 결제일로부터 14일 이내라면, 이유와 관계없이 전액 환불이 가능합니다.\n" +
        "- 결제일로부터 14일 이후에는 환불이 불가합니다.\n" +
        "\n" +
        "환불 신청하기 >" +
        "\n" +
        "\n" +
        "연장 이용\n" +
        "- 기간 내 문제가 해결되어 이용 종료를 원하신다면 남은 기간에 대해 일할 계산하여 환불이 가능합니다.";
    } else if (
      refundPolicyType === "c" ||
      refundPolicyType === null ||
      refundPolicyType === undefined
    ) {
      modifiedFaq.answer =
        "아래 환불 규정에 따라 환불이 가능합니다.\n" +
        "\n" +
        "[환불 규정]\n" +
        "\n" +
        "최초 이용\n" +
        "결제일로부터 14일 이내에는 다음 중 하나에 해당할 경우, 이유와 관계없이 전액 환불이 가능합니다.\n" +
        "- 활동지를 한 번도 진행하지 않으신 경우\n" +
        "- 2세트 이상 제공된 활동지를 진행하신 경우\n" +
        "\n" +
        "최초 이용 환불 신청하기 >" +
        "\n" +
        "\n" +
        "연장 이용\n" +
        "100일의 최초 이용 기간에 더해 추가 결제하여 연장 이용을 하신 경우, 문제가 해결되어 이용 종료를 원하신다면 남은 기간에 대해 일할 계산하여 환불이 가능합니다.\n" +
        "\n" +
        "연장 이용 환불 신청하기 >";
    } else if (refundPolicyType === "a" || refundPolicyType === "d") {
      modifiedFaq.answer =
        "아래 환불 규정에 따라 환불이 가능합니다.\n" +
        "\n" +
        "[환불 규정]\n" +
        "\n" +
        "최초 이용\n" +
        "결제일로부터 7일 이내에는 활동지를 한 번도 진행하지 않으신 경우 전액 환불이 가능합니다. 활동지를 진행하셨다면 결제 금액의 10% 차감 후 환불이 가능합니다.\n" +
        "결제일로부터 7일 이후에는 환불이 불가합니다.\n" +
        "\n" +
        "최초 이용 환불 신청하기 >" +
        "\n" +
        "\n" +
        "연장 이용\n" +
        "100일의 최초 이용 기간에 더해 추가 결제하여 연장 이용을 하신 경우, 문제가 해결되어 이용 종료를 원하신다면 남은 기간에 대해 일할 계산하여 환불이 가능합니다.\n" +
        "\n" +
        "연장 이용 환불 신청하기 >";
    }
  }

  // FAQ ID 102: Payback System
  if (faq.faqId === 102) {
    if (refundPolicyType === "a" || refundPolicyType === "d") {
      modifiedFaq.answer =
        "첫 100일을 모두 이용하신 뒤에도 변화를 느끼지 못하신 경우, 비용 전액을 돌려드리고 있습니다.\n" +
        "\n" +
        "[페이백 규정]\n" +
        "- 결제 이후 첫 100일 동안 최소 주 1회 주어진 모든 활동지를 마치며 프로그램을 꾸준히 진행하신 경우에만 페이백이 가능합니다.\n" +
        "- 첫 100일을 모두 이용하신 후, 10일 이내에 신청이 가능합니다.\n" +
        "- 페이백 제도는 첫 100일 이용에만 적용되며, 프로그램 추가 연장 이용부터는 적용되지 않습니다.\n" +
        "\n" +
        "페이백 요청은 닉네임과 회원번호를 포함해 고객센터로 넣어주세요.";
    }
  }

  return modifiedFaq;
};

/**
 * Apply conditional content to a list of FAQs based on user properties
 *
 * @param faqList The original list of FAQ items
 * @param user The user object containing pricing model and subscription information
 * @returns The list of FAQ items with appropriate content
 */
export const getConditionalFaqList = (
  faqList: CoachingFaq[],
  user?: UserType
): CoachingFaq[] => {
  if (!user || !faqList) {
    return faqList;
  }

  return faqList.map((faq) => getConditionalFaqContent(faq, user));
};
